import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../services/api';
import toast from '../../../utils/toast';

/**
 * Custom hook for centralized overtime monitoring data fetching.
 *
 * Single source of truth: ``/staff-core/overtime-monitoring/employees/``
 * - Returns the full unfiltered roster of workmen for the selected
 *   month with AR-sourced cap-eligible OT (see
 *   ``.cursor/rules/mom-72h-ot-cap-policy.mdc``).
 *
 * From that single roster the hook derives, via ``useMemo``:
 *   * ``stats``              — the dashboard-card aggregates.
 *   * ``filteredEmployees``  — the current filter view for the table.
 *   * ``forecastSummary``    — exposed verbatim from the API (the
 *                              forecast loop is server-side).
 *
 * This collapses the previously dual-endpoint architecture (cards read
 * ``realtime-stats``, table reads ``employees``) onto one network call
 * and one in-memory dataset, so the cards and the table can no longer
 * disagree. Filter changes also no longer hit the network — instant
 * UX, no server load.
 *
 * Other consumers of ``realtime-stats`` (e.g. the global
 * ExecutiveSummaryStrip / MOMComplianceSection) keep using their
 * dedicated endpoint, which now delegates to the same AR-sourced
 * kernel server-side, so they get the same fix without any frontend
 * change.
 */
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const deriveStatsFromEmployees = (employees, yearMonth) => {
  const list = employees || [];
  const total = list.length;
  const totalOvertimeHours = round2(
    list.reduce((sum, e) => sum + (Number(e.currentOtHours) || 0), 0)
  );
  const averageOvertimeHours = total > 0 ? round2(totalOvertimeHours / total) : 0;

  const byStatus = {
    compliant: 0,
    at_risk: 0,
    critical: 0,
    exceeded: 0,
    projected_exceed: 0,
    watch: 0,
    on_track: 0,
  };
  for (const emp of list) {
    const key = emp.unifiedStatus || emp.riskLevel || 'compliant';
    if (key in byStatus) byStatus[key] += 1;
    else byStatus.compliant += 1;
  }

  return {
    currentMonth: {
      yearMonth,
      totalEmployees: total,
      totalOvertimeHours,
      averageOvertimeHours,
    },
    atRiskCount: byStatus.at_risk,
    criticalCount: byStatus.critical,
    exceededCount: byStatus.exceeded,
    compliantCount: byStatus.compliant + byStatus.on_track,
    complianceRate:
      total > 0
        ? Math.round(((byStatus.compliant + byStatus.on_track) / total) * 100)
        : 0,
    projectedExceedCount: byStatus.projected_exceed,
    watchCount: byStatus.watch,
  };
};

const applyClientFilters = (employees, filters) => {
  let list = employees || [];

  if (filters.department) {
    const q = filters.department.toLowerCase();
    list = list.filter((e) => (e.department || '').toLowerCase().includes(q));
  }
  if (filters.trade) {
    const q = filters.trade.toLowerCase();
    list = list.filter((e) => (e.trade || '').toLowerCase().includes(q));
  }
  if (filters.search) {
    const q = filters.search.toLowerCase();
    list = list.filter(
      (e) =>
        (e.staffId || '').toLowerCase().includes(q) ||
        (e.fullName || '').toLowerCase().includes(q) ||
        (e.firstName || '').toLowerCase().includes(q) ||
        (e.lastName || '').toLowerCase().includes(q)
    );
  }
  if (filters.riskLevel) {
    if (filters.riskLevel === 'action_required') {
      list = list.filter((e) =>
        ['exceeded', 'critical'].includes(e.unifiedStatus)
      );
    } else if (filters.riskLevel === 'watch_list') {
      list = list.filter((e) =>
        ['at_risk', 'projected_exceed', 'watch'].includes(e.unifiedStatus)
      );
    } else {
      list = list.filter((e) => e.unifiedStatus === filters.riskLevel);
    }
  }
  if (filters.hasDiscrepancy === 'true') {
    list = list.filter((e) => !!e.hasUndeclaredOt);
  } else if (filters.hasDiscrepancy === 'false') {
    list = list.filter((e) => !e.hasUndeclaredOt);
  }

  return list;
};

const useOvertimeData = () => {
  const [employees, setEmployees] = useState([]);
  const [forecastSummary, setForecastSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [isCurrentMonth, setIsCurrentMonth] = useState(true);
  // Cache-warmer status powers the "Data as of X · refreshes every 5 min"
  // indicator on the employees tab. Populated initially from the heavy
  // employees response (no extra round-trip on first paint) and then
  // refreshed every ~60s via the dedicated lightweight endpoint so the
  // indicator stays current without re-running the full snapshot build.
  const [cacheStatus, setCacheStatus] = useState(null);

  const [filters, setFilters] = useState({
    department: '',
    riskLevel: '',
    search: '',
    hasDiscrepancy: '',
    trade: '',
  });

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-SG', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  }, []);

  // Always fetch the unfiltered roster so the cards (computed
  // client-side) reflect the global state, not the current filter view.
  // Filtering happens entirely client-side in ``filteredEmployees``.
  //
  // ``timeout: 0`` disables the global 30 s axios cap for this single
  // request. The backend kernel here (AR-sourced cap-eligible OT + live
  // LCS overlay for un-normalised days + 72-hour forecast for the
  // current month) is genuinely long-running -- 40-60 s is normal for
  // a busy month, and a partial result is meaningless. Letting the
  // request run to completion is correct; the browser/TCP layer will
  // still abort a truly hung connection. We DO NOT raise the global
  // cap because every other CRUD call benefits from a strict ceiling.
  const fetchEmployees = useCallback(async (yearMonth, { forceRefresh = false } = {}) => {
    try {
      // Initial load + month change must NOT force a rebuild — they read
      // the shared, Celery-warmed snapshot cache so the request returns in
      // well under the Cloudflare ~100s deadline. ``force_refresh`` is only
      // ever set by an explicit manual refresh (and even then the manual
      // path normally goes through the async ``refetch`` below).
      const params = { year_month: yearMonth };
      if (forceRefresh) {
        params.force_refresh = 'true';
      }
      const response = await api.get(
        '/staff-core/overtime-monitoring/employees/',
        { params, timeout: 0 }
      );
      setEmployees(response.data.employees || []);
      setIsCurrentMonth(response.data.isCurrentMonth !== false);
      setForecastSummary(response.data.forecastSummary || null);
      if (response.data.cacheStatus) {
        setCacheStatus(response.data.cacheStatus);
      }
      return response.data;
    } catch (err) {
      console.error('Error fetching employees overtime status:', err);
      throw err;
    }
  }, []);

  // Stateless per-month fetch for the multi-month Excel export. Unlike
  // ``fetchEmployees`` it does NOT mutate page state — it just returns
  // the roster for an arbitrary month so the export modal can pull a
  // handful of months in sequence and stitch them into one workbook.
  //
  // The active table filters are forwarded as server-side query params
  // so every exported month is scoped exactly like the on-screen view
  // (e.g. "Factory department, Critical only" across Jan–Jun). Same
  // ``timeout: 0`` rationale as ``fetchEmployees`` — the snapshot kernel
  // is genuinely long-running and a partial result is meaningless.
  const fetchEmployeesForMonth = useCallback(async (yearMonth, filterParams = {}) => {
    // No auto force_refresh — the export reads the warm snapshot cache for
    // every month (forcing a synchronous rebuild per month would make a
    // multi-month export pathologically slow and risk the 524).
    const params = { year_month: yearMonth };
    if (filterParams.department) params.department = filterParams.department;
    if (filterParams.trade) params.trade = filterParams.trade;
    if (filterParams.riskLevel) params.risk_level = filterParams.riskLevel;
    if (filterParams.hasDiscrepancy) params.has_discrepancy = filterParams.hasDiscrepancy;
    if (filterParams.search) params.search = filterParams.search;
    const response = await api.get(
      '/staff-core/overtime-monitoring/employees/',
      { params, timeout: 0 }
    );
    return {
      employees: response.data.employees || [],
      isCurrentMonth: response.data.isCurrentMonth !== false,
    };
  }, []);

  // Lightweight cache-status poll. Hits a dedicated endpoint that
  // only reads the Redis marker keys (no snapshot rebuild), so it's
  // safe to call every 60 s while the user has the dashboard open.
  const fetchCacheStatus = useCallback(async (yearMonth) => {
    try {
      const response = await api.getOvertimeCacheStatus({ year_month: yearMonth });
      setCacheStatus(response.data || null);
      return response.data;
    } catch (err) {
      // Indicator is decorative — failures must not surface as toasts
      // or page-level errors. Silent log only.
      console.debug('Cache-status poll failed:', err?.message || err);
      return null;
    }
  }, []);

  const loadAllData = useCallback(async (yearMonth, { forceRefresh = false } = {}) => {
    setLoading(true);
    setError(null);
    const monthToLoad = yearMonth || selectedMonth;
    try {
      // Alerts tab derives from this same live roster — no separate
      // legacy /alerts/ fetch (that path is orphaned from digests).
      await fetchEmployees(monthToLoad, { forceRefresh });
    } catch (err) {
      setError(err.message || 'Failed to load overtime data');
      toast.error('❌ Failed to load overtime data');
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees, selectedMonth]);

  // Async manual refresh (avoids the synchronous force-refresh 524):
  //   1. POST employees/refresh/ → enqueues a Celery snapshot rebuild.
  //   2. Poll cache-status until snapshot_status === 'ready'.
  //   3. Re-read the now-warm snapshot (no force_refresh).
  // Falls back to a synchronous force-refresh if the async endpoint is
  // unavailable (older backend / broker down) so the button always works.
  const requestSnapshotRebuild = useCallback(async (yearMonth) => {
    await api.refreshEmployeesOvertimeSnapshot({ year_month: yearMonth });
  }, []);

  const waitForSnapshotReady = useCallback(
    async (yearMonth, { intervalMs = 2500, timeoutMs = 90_000 } = {}) => {
      const start = Date.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const status = await fetchCacheStatus(yearMonth);
        if (status && status.snapshot_status === 'ready') return true;
        if (Date.now() - start > timeoutMs) return false;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    },
    [fetchCacheStatus]
  );

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await requestSnapshotRebuild(selectedMonth);
      await waitForSnapshotReady(selectedMonth);
      await fetchEmployees(selectedMonth);
    } catch (asyncErr) {
      // Async path unavailable — fall back to a synchronous force refresh.
      console.debug(
        'Async OT snapshot refresh failed, falling back to sync force refresh:',
        asyncErr?.message || asyncErr
      );
      try {
        await fetchEmployees(selectedMonth, { forceRefresh: true });
      } catch (err) {
        setError(err.message || 'Failed to refresh overtime data');
        toast.error('❌ Failed to refresh overtime data');
      }
    } finally {
      setLoading(false);
    }
  }, [
    selectedMonth,
    requestSnapshotRebuild,
    waitForSnapshotReady,
    fetchEmployees,
  ]);

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const changeMonth = useCallback((newMonth) => {
    setSelectedMonth(newMonth);
  }, []);

  const getEmployeeHistory = useCallback(async (staffId) => {
    try {
      const response = await api.get(`/staff-core/overtime-monitoring/employee/${staffId}/history/`);
      return response.data;
    } catch (err) {
      console.error('Error fetching employee history:', err);
      toast.error('❌ Failed to load employee history');
      return null;
    }
  }, []);

  const getEmployeeDailyBreakdown = useCallback(async (staffId, yearMonth) => {
    try {
      const params = yearMonth ? { year_month: yearMonth } : {};
      const response = await api.get(`/staff-core/overtime-monitoring/employee/${staffId}/daily-breakdown/`, { params });
      return response.data;
    } catch (err) {
      console.error('Error fetching employee daily breakdown:', err);
      toast.error('❌ Failed to load daily OT breakdown');
      return null;
    }
  }, []);

  // Notification-coverage audit for the OT / compliance digests. Reads
  // the per-recipient delivery records + coverage gaps for the selected
  // month. Lightweight (indexed reads, no snapshot rebuild) so the
  // default 30s axios timeout is fine here.
  const getNotificationCoverage = useCallback(async (params = {}) => {
    try {
      const response = await api.get(
        '/staff-core/overtime-monitoring/notification-coverage/',
        { params }
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching notification coverage:', err);
      toast.error('❌ Failed to load notification coverage');
      return null;
    }
  }, []);

  const getComplianceReport = useCallback(async (params = {}) => {
    try {
      // Same long-running profile as fetchEmployees -- the compliance
      // report runs the same kernel across the requested window.
      // Disable the global timeout for the same reasons documented on
      // fetchEmployees.
      const response = await api.get(
        '/staff-core/overtime-monitoring/compliance-report/',
        { params, timeout: 0 }
      );
      return response.data;
    } catch (err) {
      console.error('Error fetching compliance report:', err);
      toast.error('❌ Failed to generate compliance report');
      return null;
    }
  }, []);

  // Initial load + month change. Filter changes do NOT trigger a
  // fetch — they're applied to the in-memory roster.
  useEffect(() => {
    loadAllData(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  // Poll the lightweight cache-status endpoint every 60 s so the
  // "Data as of X" indicator stays current without re-running the
  // heavy snapshot. Only enabled on the current SGT month —
  // historical months don't use the live overlay so the marker
  // would be permanently empty.
  useEffect(() => {
    if (!isCurrentMonth) return undefined;
    const intervalMs = 60_000;
    const tick = () => {
      fetchCacheStatus(selectedMonth);
    };
    const handle = setInterval(tick, intervalMs);
    return () => clearInterval(handle);
  }, [selectedMonth, isCurrentMonth, fetchCacheStatus]);

  // Derived data — single source of truth for the cards and the table.
  const stats = useMemo(
    () => deriveStatsFromEmployees(employees, selectedMonth),
    [employees, selectedMonth]
  );

  const filteredEmployees = useMemo(
    () => applyClientFilters(employees, filters),
    [employees, filters]
  );

  return {
    stats,
    employees: filteredEmployees,
    allEmployees: employees,
    forecastSummary,
    loading,
    error,
    filters,
    selectedMonth,
    isCurrentMonth,
    monthOptions,
    cacheStatus,
    refetch,
    updateFilters,
    changeMonth,
    fetchEmployeesForMonth,
    getEmployeeHistory,
    getEmployeeDailyBreakdown,
    getComplianceReport,
    getNotificationCoverage,
  };
};

export default useOvertimeData;
